import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, blend, cellsOf, chooser, cycle, find, fitForm, midi, noteName, pc, pcs, phrase, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; tones: readonly string[]; hold: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  Am: { root: 'A1', tones: ['A3', 'C4', 'E4'], hold: ['A3', 'C4', 'E4'] },
  G: { root: 'G1', tones: ['G3', 'B3', 'D4'], hold: ['G3', 'B3', 'D4'] },
  F: { root: 'F1', tones: ['F3', 'A3', 'C4'], hold: ['F3', 'A3', 'C4'] },
  Em: { root: 'E1', tones: ['E3', 'G3', 'B3'], hold: ['G3', 'B3', 'E4'] },
  Dm: { root: 'D2', tones: ['D3', 'F3', 'A3'], hold: ['F3', 'A3', 'D4'] },
};

const PROGS = [
  ['Am', 'G', 'Am', 'G'],
  ['Am', 'F', 'G', 'Am'],
  ['Am', 'Am', 'G', 'F'],
  ['Dm', 'Am', 'G', 'Am'],
  ['Am', 'G', 'F', 'Em'],
] as const;

const LOA: Note[][] = [
  [[0, 'E4', 3], [3, 'E4', 1, 0.7], [4, 'D4', 2], [6, 'C4', 2], [8, 'D4', 4], [12, 'E4', 2], [14, 'C4', 2]],
  [[0, 'B3', 4], [4, 'C4', 2], [6, 'D4', 2], [8, 'B3', 3], [11, 'A3', 1, 0.7], [12, 'G3', 4]],
  [[0, 'E4', 2], [2, 'D4', 2], [4, 'C4', 2], [6, 'D4', 2], [8, 'E4', 4], [12, 'A4', 4]],
  [[0, 'G4', 2], [2, 'E4', 2], [4, 'D4', 4], [8, 'B3', 2], [10, 'D4', 2], [12, 'E4', 4]],
];

const KEYS = [0, -2, 2, -3, 3, -5, 1] as const;
const MINOR = [0, 2, 3, 5, 7, 8, 10] as const;
const MARCANTES = ['X.....x.X.....x.', 'X.....x.X...x...', 'X.....x.X.x...x.'] as const;
const MEIOES = ['....X..o....X.x.', '..x.X..o..x.X...', '....X.x.....X.xo'] as const;
const REPIQUES = ['.o.o.o.o.o.o.o.o', 'oo.o.oo.oo.o.oo.'] as const;
const GONGUES = [
  [[0, 0], [2, 1], [3, 1], [6, 0], [8, 0], [10, 1], [11, 1], [14, 0]],
  [[0, 0], [3, 1], [4, 1], [6, 0], [8, 0], [11, 1], [12, 1], [14, 1]],
] as const;
const CAIXAS = ['x-o-X-o-x-o-X-oo', 'x-o-X-o-x-oxX-o-'] as const;
const AGBES = ['x-ox-x-ox-ox-x-o', 'X-x-x-x-X-x-x-x-'] as const;
const VIRADA = 'X.X.X.X.XXXXXXXX';
const CHAMADA = 'X...X...X.X.XXXX';

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 3 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 2 },
  ],
};

function under(note: string, tones: readonly number[]): string {
  const m = midi(note);
  const scale = blend([...MINOR, 9], tones);
  for (const d of [3, 4]) if (scale.includes(pc(m - d)) && tones.includes(pc(m - d))) return noteName(m - d);
  for (const d of [3, 4]) if (scale.includes(pc(m - d))) return noteName(m - d);
  return noteName(m - 3);
}

function score(seed: string, p: Params): Score {
  const ch = chooser(MARACATU, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const marcP = ch.pick(MARCANTES);
  const meiaoP = ch.pick(MEIOES);
  const repP = ch.pick(REPIQUES);
  const gongue = ch.pick(GONGUES);
  const caixaP = ch.pick(CAIXAS);
  const agbeP = ch.pick(AGBES);
  const loa = ch.orig
    ? LOA
    : phrase(
        ch.rng,
        LOA.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs(c.tones) }]),
        [...MINOR],
        [55, 69],
        64,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);
  const low = (n: string) => {
    let m = midi(tr(n));
    while (m < 31) m += 12;
    while (m > 42) m -= 12;
    return noteName(m);
  };

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 54, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const brk = find(form, 'break');
  const rng = createRng(`maracatu-${seed}`);
  const human = (v: number) => v * (0.92 + rng.float() * 0.16);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum = 0) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes, strum });
  const gong = (bar: number, vel: number) => {
    for (const [step, high] of gongue) play('gongue', bar, step, high === 1 ? 'A5' : 'E5', s * 3, human(vel * (step % 8 === 0 ? 1 : 0.8)));
  };
  const apito = (bar: number, step: number) => {
    play('apito', bar, step, 'E7', s * 0.9, 0.85);
    play('apito', bar, step + 2, 'E7', s * 0.9, 0.85);
  };

  const autos: Score['autos'] = [
    { v: 'bandLp', points: [[g.at(0), 900], [g.at(intro), 5200]] },
    { v: 'bandLp', points: [[g.at(total - 1), 5200], [g.at(total), 1400]] },
  ];

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const end = b.bar === total - 1;
    const c = end ? prog[0]! : prog[i]!;
    const tones = pcs(c.tones);
    const drop = b.name === 'drop' || b.name === 'drop2';
    const nation = drop || b.name === 'A' || (b.name === 'outro' && b.index < b.bars - 2);
    const hold = c.hold.map(tr);

    if (end) {
      hit('alfaia', b.bar, 0, 1);
      hit('meiao', b.bar, 0, 1);
      hit('impact', b.bar, 0, 0.85);
      play('gongue', b.bar, 0, 'E5', s * 6, 0.9);
      play('sub', b.bar, 0, low(c.root), g.barLength * 0.7, 0.9);
      chord('coro', b.bar, 0, hold, g.barLength * 0.9, 0.7);
      continue;
    }

    if (nation) {
      const fill = b.last && b.name === 'A';
      for (const h of hits(fill ? 'X.......X.......' : marcP)) hit('alfaia', b.bar, h.step, human(h.vel));
      for (const h of hits(meiaoP)) if (!fill || h.step < 8) hit('meiao', b.bar, h.step, human(h.vel));
      if (fill) for (const h of hits('........X.X.XXXX')) hit('repique', b.bar, h.step, 0.5 + (0.45 * h.step) / 16);
      else if (drop && e >= 0.6) for (const h of hits(repP)) hit('repique', b.bar, h.step, human(h.vel * (0.6 + 0.4 * e)));
      gong(b.bar, drop ? 0.95 : 0.85);
      for (const h of hits(caixaP)) hit('caixa', b.bar, h.step, human(h.vel * (drop ? 1 : 0.75)));
      for (const h of hits(agbeP)) hit('agbe', b.bar, h.step, human(h.vel * (drop ? 1 : 0.8)));
      const root = low(c.root);
      for (const h of hits(fill ? 'X.......X.......' : marcP)) {
        play('sub', b.bar, h.step, root, (h.step === 0 || h.step === 8 ? 5 : 2.5) * s, h.vel * 0.9);
      }
      chord('drone', b.bar, 0, hold, g.barLength * 0.98, drop ? 0.75 : 0.6, 0.02);
    } else if (b.name === 'break') {
      if (b.index === 0) apito(b.bar, 0);
      gong(b.bar, 0.9);
      for (const h of hits(agbeP)) hit('agbe', b.bar, h.step, human(h.vel * 0.9));
      chord('coro', b.bar, 0, hold, g.barLength * 0.95, 0.65, 0.015);
      play('sub', b.bar, 0, low(c.root), g.barLength * 0.9, 0.55);
      if (b.last) {
        for (const h of hits(VIRADA)) hit('repique', b.bar, h.step, 0.4 + (0.55 * h.step) / 16);
        for (const h of hits('X...X...X...X.XX')) hit('caixa', b.bar, h.step, 0.5 + (0.45 * h.step) / 16);
        apito(b.bar, 8);
      } else if (b.index >= 1) {
        for (const h of hits('X.......X.......')) hit('repique', b.bar, h.step, human(0.55));
      }
    } else if (b.name === 'intro') {
      if (b.index === 0) apito(b.bar, 0);
      gong(b.bar, b.index === 0 ? 0.7 : 0.8);
      if (b.index >= 1) for (const h of hits(agbeP)) hit('agbe', b.bar, h.step, human(h.vel * 0.7));
      if (b.index >= b.bars / 2) chord('drone', b.bar, 0, hold, g.barLength * 0.98, 0.45, 0.02);
      if (b.last) for (const h of hits(CHAMADA)) hit('repique', b.bar, h.step, 0.45 + (0.45 * h.step) / 16);
    } else if (b.name === 'outro') {
      gong(b.bar, 0.75);
      for (const h of hits('X.......X.......')) hit('alfaia', b.bar, h.step, human(0.85));
      for (const h of hits(agbeP)) hit('agbe', b.bar, h.step, human(h.vel * 0.6));
      chord('drone', b.bar, 0, hold, g.barLength * 0.98, 0.5, 0.02);
    }

    const row = loa[i]!;
    const call = i < 2;
    const sing = (vel: number, coro: boolean) => {
      for (const [step, note, len, v] of row) {
        const n = tr(note);
        play('loa', b.bar, step, n, len * s * 0.9, human((v ?? 0.95) * vel));
        if (coro && !call) {
          play('coro', b.bar, step, n, len * s * 0.88, (v ?? 0.9) * vel * 0.8);
          play('coro', b.bar, step, tr(under(note, tones)), len * s * 0.88, (v ?? 0.9) * vel * 0.65);
        }
      }
    };
    if (b.name === 'drop') sing(1, true);
    if (b.name === 'drop2') sing(1, true);
    if (b.name === 'A' && e >= 0.6 && call) sing(0.8, false);
    if (b.name === 'break' && !b.last && b.index < 2) sing(0.75, true);
  }

  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('impact', bar, 0, 1);
    if (bar > 0) apito(bar - 1, 12);
  }
  if (brk !== undefined && drop2 === undefined) apito(brk, 0);

  return {
    id: trackId(MARACATU, seed, p),
    template: MARACATU.id,
    seed,
    params: p,
    title: ch.orig ? MARACATU.ref.title : titleOf(seed),
    genre: MARACATU.genre,
    bpm: p.bpm,
    swing: 54,
    lufs: -10.5,
    key,
    form,
    mix: { reverbDecay: 2.6, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.18, drive: 1.6, softness: 1.4, pumpDepth: 0.3, pumpRelease: 0.28 },
    events,
    autos,
    accents: { alfaia: 'kick', meiao: 'snare', impact: 'drop' },
  };
}

export const MARACATU: Template = {
  id: 'maracatu',
  genre: 'maracatu de baque virado',
  bpm: [104, 124],
  ref: { seed: 'cortejo', title: 'Cortejo', params: { bpm: 112, bars: 28, energy: 0.55 } },
  score,
};
